import { storage } from '@uhg-abyss/web/tools/storage';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { Toggle } from '@uhg-abyss/web/ui/Toggle';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useHsidUuid } from '../../hooks/useHsidUuid';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useSearchStore } from '../../store/useSearchStore';
import { SearchState } from '../../store/useSearchStore/searchStore';
import { getUiMessageFromLagoon } from '../../utils/common';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import {
  StyledText,
  compareSharePopoverStyles,
} from './CompareShareToggle.styled';

interface Props {
  title: string | '';
  isChecked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  dataTestId?: string;
  expandedProviderListView?: boolean;
}

export const CompareShareToggle = ({
  title,
  isChecked,
  onChange,
  dataTestId,
  expandedProviderListView: _expandedProviderListView = false,
}: Props) => {
  const lagoonData = useLagoon('ui-messaging')();
  const compareProvidersText = getUiMessageFromLagoon(
    ConstantsLagoon.UI_MESSAGING.COMPARE_PROVIDERS,
    lagoonData
  );

  const { setSearchStore } = useSearchStore(
    useShallow((state: SearchState) => ({
      setSearchStore: state.setSearchStore,
    }))
  );
  useEffect(() => {
    setSearchStore({
      compareToggleOn: isChecked,
    });
  }, [isChecked]);
  const getAndShareInformationOnCostText = getUiMessageFromLagoon(
    ConstantsLagoon.UI_MESSAGING.GET_AND_SHARE_INFORMATION_ON_COST,
    lagoonData
  );
  const [isNewComparePopupFlowEnabled, enablePopoverPriorityFlow] =
    useFeatureFlag([
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_COMPARE_POPOVER_FLOW,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_POPOVER_PRIORITY_FLOW,
    ]);
  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );
  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );
  const hsidUuid = useHsidUuid();
  const [getEducationalContentStatus, setEducationalContentStatus] =
    useSaveEducationalContentStatus({});
  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;
  const isCoachmarkShownThisSession = useRef(false);
  const [isCoachmarkOpen, setIsCoachmarkOpen] = useState(false);

  useEffect(() => {
    if (enablePopoverPriorityFlow) {
      if (showCompareCoachMark() && !isCoachmarkShownThisSession.current) {
        isCoachmarkShownThisSession.current = true;
        setIsCoachmarkOpen(true);
        setEducationalContentStatus({
          variables: {
            hasViewedContent: !!educationalContentStore?.hasViewedContent,
            showCompareCoachmarkWeb: false,
            ...(hsidUuid && { userId: hsidUuid }),
            isSuperUser,
          },
        });
        setTimeout(() => {
          const activeElement = document.activeElement as HTMLElement;
          if (activeElement) {
            activeElement.blur();
          }
        }, 0);
      }
    } else {
      if (educationalContentStore?.showCompareCoachmarkWeb) {
        setTimeout(() => {
          const activeElement = document.activeElement as HTMLElement;
          if (activeElement) {
            activeElement.blur();
          }
        }, 0);
      }
    }
  }, [educationalContentStore?.showCompareCoachmarkWeb]);

  const handleOpenChange = () => {
    if (enablePopoverPriorityFlow) {
      isCoachmarkShownThisSession.current = false;
      setIsCoachmarkOpen(false);
    } else {
      setEducationalContentStatus({
        variables: {
          hasViewedContent: !!educationalContentStore?.hasViewedContent,
          showCompareCoachmarkWeb: false,
          ...(hsidUuid && { userId: hsidUuid }),
          isSuperUser,
        },
      });
    }
    sessionStorage.removeItem(
      Constants.STORAGE_KEYS.SESSION.HAS_DISMISSED_COMPARE_COACHMARK_WEB
    );
  };

  useEffect(() => {
    if (getEducationalContentStatus?.data?.saveEducationalContentStatus) {
      const { saveEducationalContentStatus } =
        getEducationalContentStatus?.data;
      setEducationalContentStore({
        ...educationalContentStore,
        hasViewedContent: saveEducationalContentStatus?.hasViewedContent,
        showCompareModal:
          saveEducationalContentStatus?.showCompareModal === null
            ? educationalContentStore?.showCompareModal
            : saveEducationalContentStatus?.showCompareModal,
        showCompareCoachmarkWeb:
          saveEducationalContentStatus?.showCompareCoachmarkWeb,
      });
    }
  }, [getEducationalContentStatus]);
  const isGEHAV2 = shouldUseGehaV2Styling();
  const showCompareCoachMark = () => {
    const hasDismissed =
      sessionStorage.getItem(
        Constants.STORAGE_KEYS.SESSION.HAS_DISMISSED_COMPARE_COACHMARK_WEB
      ) !== 'y';
    const shouldShowCoachMark =
      educationalContentStore?.showCompareCoachmarkWeb;
    const isCompareModalHidden = !educationalContentStore?.showCompareModal;

    if (isChecked) return false;

    return isNewComparePopupFlowEnabled
      ? hasDismissed && shouldShowCoachMark && isCompareModalHidden
      : shouldShowCoachMark;
  };

  return (
    <Flex css={{ gap: '4px', alignItems: 'center' }}>
      <StyledText
        as="span"
        color={isGEHAV2 ? '$core.color.brand.100' : 'black'}
        data-testid={`${dataTestId}-title`}
      >
        {title}
      </StyledText>

      <Popover
        content={getAndShareInformationOnCostText}
        css={{
          ...compareSharePopoverStyles,
          display: 'flex',
          alignItems: 'center',
          '@media print': {
            display: 'none !important',
          },
        }}
        data-testid="smart-choice-coachmark"
        onOpenChange={handleOpenChange}
        open={
          enablePopoverPriorityFlow ? isCoachmarkOpen : showCompareCoachMark()
        }
        position="bottom"
        showClose={true}
        title={compareProvidersText}
      >
        <div style={{ display: 'none' }} />
      </Popover>
      <Toggle
        aria-label={title}
        data-testid={dataTestId}
        isChecked={isChecked}
        label={''}
        onChange={(event) => {
          if (event.target.checked && isNewComparePopupFlowEnabled) {
            handleOpenChange();
          }
          onChange(event);
        }}
      />
    </Flex>
  );
};
